import { ThemeEvents } from '@theme/events';

const CARD_SELECTOR = '[data-townwine-inventory-card]';
const PRODUCT_SELECTOR = '[data-townwine-inventory-product]';
const DEFAULT_PROXY_BASE = '/apps/townwine-follow';
const DEFAULT_MAX_PER_PERSON = 6;
const INVENTORY_RETRY_DELAY_MS = 1500;
const INVENTORY_MAX_RETRY_COUNT = 4;

const inventorySnapshotStore = new Map();
const inventoryRetryTimers = new Map();
const inventoryRetryCounts = new Map();
let refreshTimer = 0;
let domObserver;

function normalizeProxyBase(value) {
  const trimmedValue = String(value || '').trim();

  if (trimmedValue === '') {
    return DEFAULT_PROXY_BASE;
  }

  return trimmedValue.endsWith('/') ? trimmedValue.slice(0, -1) : trimmedValue;
}

function parseVariantIds(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatCount(value) {
  return new Intl.NumberFormat('ko-KR').format(Number(value) || 0);
}

function getProgressPercent(snapshot) {
  return Math.max(0, Math.min(100, Number(snapshot?.progressPercent) || 0));
}

function hasPendingSoldCount(snapshot) {
  return Boolean(snapshot?.soldCountPending);
}

function getRemainingLabel(snapshot) {
  if (!snapshot) {
    return '재고 정보 확인 중';
  }

  if (!snapshot.inventoryTracked) {
    return snapshot.availableForSale ? '재고 확인 중' : '공구 마감';
  }

  if (snapshot.remainingInventory <= 0) {
    return '재고 소진';
  }

  return `재고 ${formatCount(snapshot.remainingInventory)}병 남음`;
}

function getCardSummary(snapshot) {
  if (!snapshot) {
    return '판매 수량 확인 중';
  }

  if (hasPendingSoldCount(snapshot)) {
    return '판매 수량 확인 중';
  }

  if (!snapshot.inventoryTracked) {
    return `${formatCount(snapshot.soldCount)}병 판매`;
  }

  if (snapshot.totalInventory <= 0 && snapshot.soldCount > 0) {
    return `${formatCount(snapshot.soldCount)}병 판매 완료`;
  }

  if (snapshot.totalInventory <= 0) {
    return '판매 수량 확인 중';
  }

  return `${formatCount(snapshot.soldCount)} / ${formatCount(snapshot.totalInventory)}병 판매`;
}

function getProductBadge(snapshot) {
  if (!snapshot?.inventoryTracked) {
    return '재고 연동 중';
  }

  if (hasPendingSoldCount(snapshot)) {
    return snapshot.remainingInventory <= 0 ? '재고 소진' : '판매 집계 중';
  }

  if (snapshot.totalInventory <= 0 && snapshot.soldCount > 0) {
    return '전량 판매 완료';
  }

  return `판매 ${formatCount(getProgressPercent(snapshot))}%`;
}

function getProductHelper(snapshot) {
  if (!snapshot?.inventoryTracked) {
    return '재고 연동 준비 중';
  }

  if (snapshot.remainingInventory <= 0) {
    return '전량 판매 완료';
  }

  if (hasPendingSoldCount(snapshot)) {
    return '판매 수량 집계 중';
  }

  return '현재 판매 가능';
}

function setText(scope, selector, value) {
  scope.querySelectorAll(selector).forEach((element) => {
    if (element instanceof HTMLElement) {
      element.textContent = value;
    }
  });
}

function syncProgressClasses(element, progressPercent) {
  element.classList.toggle('pg--ur', progressPercent >= 75 && progressPercent < 100);
  element.classList.toggle('pg--ok', progressPercent >= 100);
}

function getSnapshotForVariantId(variantId) {
  return inventorySnapshotStore.get(String(variantId || '').trim()) || null;
}

function getAggregateCardSnapshot(variantIdsValue) {
  const variantIds = parseVariantIds(variantIdsValue);

  if (!variantIds.length) {
    return null;
  }

  const snapshots = variantIds
    .map((variantId) => getSnapshotForVariantId(variantId))
    .filter(Boolean);

  if (!snapshots.length) {
    return null;
  }

  if (snapshots.length === 1) {
    return snapshots[0];
  }

  let inventoryTracked = false;
  let soldCount = 0;
  let totalInventory = 0;
  let remainingInventory = 0;
  let availableForSale = false;
  let inventoryPolicy = 'DENY';
  let soldCountPending = false;

  snapshots.forEach((snapshot) => {
    soldCount += Number(snapshot?.soldCount) || 0;
    soldCountPending = soldCountPending || hasPendingSoldCount(snapshot);
    availableForSale =
      availableForSale ||
      Boolean(snapshot?.availableForSale) ||
      (Number(snapshot?.remainingInventory) || 0) > 0;

    if (snapshot?.inventoryTracked) {
      inventoryTracked = true;
      totalInventory += Math.max(0, Number(snapshot.totalInventory) || 0);
      remainingInventory += Math.max(0, Number(snapshot.remainingInventory) || 0);

      if (String(snapshot.inventoryPolicy || '').toUpperCase() !== 'DENY') {
        inventoryPolicy = String(snapshot.inventoryPolicy || inventoryPolicy);
      }
    }
  });

  const progressPercent =
    !soldCountPending && totalInventory > 0
      ? (soldCount * 100) / totalInventory
      : soldCount > 0 && !availableForSale
        ? 100
        : 0;

  return {
    ...snapshots[0],
    availableForSale,
    inventoryPolicy,
    inventoryTracked,
    legacyVariantId: variantIds[0],
    progressPercent,
    remainingInventory,
    soldCount,
    soldCountPending,
    totalInventory,
  };
}

function updateCardWidget(widget, snapshot) {
  if (!(widget instanceof HTMLElement) || !snapshot) {
    return;
  }

  const progressPercent = getProgressPercent(snapshot);
  const progressBar = widget.querySelector('[data-inventory-progress-bar]');

  if (progressBar instanceof HTMLElement) {
    progressBar.style.width = `${progressPercent}%`;
  }

  syncProgressClasses(widget, progressPercent);
  setText(widget, '[data-inventory-summary]', getCardSummary(snapshot));
  setText(widget, '[data-inventory-caption]', getRemainingLabel(snapshot));
}

function updatePurchaseLimit(widget, snapshot) {
  const quantityInput = widget.querySelector('input[name="quantity"]');
  const quantityHint = widget.querySelector('[data-inventory-purchase-limit]');

  if (!(quantityInput instanceof HTMLInputElement)) {
    return;
  }

  const perPersonLimit = Number(widget.dataset.maxPerPerson || DEFAULT_MAX_PER_PERSON);
  let maxQuantity = perPersonLimit;

  if (snapshot?.inventoryTracked && snapshot.inventoryPolicy === 'DENY') {
    const remainingInventory = Number(snapshot.remainingInventory);
    const safeRemainingInventory = Number.isFinite(remainingInventory)
      ? remainingInventory
      : perPersonLimit;

    maxQuantity = Math.max(1, Math.min(perPersonLimit, safeRemainingInventory));
  }

  quantityInput.max = String(maxQuantity);

  if (Number(quantityInput.value || 1) > maxQuantity) {
    quantityInput.value = String(maxQuantity);
  }

  if (quantityHint instanceof HTMLElement) {
    quantityHint.textContent = `1인 최대 ${formatCount(maxQuantity)}병 가능`;
  }
}

function updateProductWidget(widget, snapshot) {
  if (!(widget instanceof HTMLElement) || !snapshot) {
    return;
  }

  const progressPercent = getProgressPercent(snapshot);
  const progressContainer = widget.querySelector('[data-inventory-progress-container]');
  const progressBar = widget.querySelector('[data-inventory-progress-bar]');

  if (progressContainer instanceof HTMLElement) {
    syncProgressClasses(progressContainer, progressPercent);
  }

  if (progressBar instanceof HTMLElement) {
    progressBar.style.width = `${progressPercent}%`;
  }

  widget.dataset.selectedVariantId = snapshot.legacyVariantId || widget.dataset.selectedVariantId || '';

  const soldCountValue = hasPendingSoldCount(snapshot) ? '...' : formatCount(snapshot.soldCount);
  const totalInventoryValue = hasPendingSoldCount(snapshot)
    ? '...'
    : formatCount(snapshot.totalInventory);

  setText(widget, '[data-inventory-badge]', getProductBadge(snapshot));
  setText(widget, '[data-inventory-sold]', soldCountValue);
  setText(widget, '[data-inventory-total]', totalInventoryValue);
  setText(widget, '[data-inventory-remaining]', formatCount(snapshot.remainingInventory));
  setText(widget, '[data-inventory-helper]', getProductHelper(snapshot));

  updatePurchaseLimit(widget, snapshot);
}

function bindProductWidget(widget) {
  if (!(widget instanceof HTMLElement) || widget.dataset.inventoryBound === 'true') {
    return;
  }

  widget.dataset.inventoryBound = 'true';
  widget.addEventListener('change', (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || target.name !== 'id') {
      return;
    }

    widget.dataset.selectedVariantId = target.value;
    updateProductWidget(widget, getSnapshotForVariantId(target.value));
  });
}

function collectInventoryGroups() {
  const groups = new Map();

  document.querySelectorAll(CARD_SELECTOR).forEach((widget) => {
    if (!(widget instanceof HTMLElement)) {
      return;
    }

    const variantIds = parseVariantIds(widget.dataset.variantIds || widget.dataset.variantId);

    if (!variantIds.length) {
      return;
    }

    const proxyBase = normalizeProxyBase(widget.dataset.proxyBase);
    const group = groups.get(proxyBase) || {
      cards: [],
      products: [],
      variantIds: new Set(),
    };

    group.cards.push(widget);
    variantIds.forEach((variantId) => {
      group.variantIds.add(variantId);
    });
    groups.set(proxyBase, group);
  });

  document.querySelectorAll(PRODUCT_SELECTOR).forEach((widget) => {
    if (!(widget instanceof HTMLElement)) {
      return;
    }

    bindProductWidget(widget);

    const proxyBase = normalizeProxyBase(widget.dataset.proxyBase);
    const group = groups.get(proxyBase) || {
      cards: [],
      products: [],
      variantIds: new Set(),
    };

    group.products.push(widget);
    parseVariantIds(widget.dataset.variantIds).forEach((variantId) => {
      group.variantIds.add(variantId);
    });
    groups.set(proxyBase, group);
  });

  return groups;
}

async function fetchInventorySnapshots(proxyBase, variantIds) {
  const url = new URL(`${proxyBase}/inventory`, window.location.origin);

  variantIds.forEach((variantId) => {
    url.searchParams.append('variantId', variantId);
  });

  const response = await fetch(url.toString(), {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Inventory request failed with status ${response.status}`);
  }

  const payload = await response.json();
  const snapshots = payload?.snapshots && typeof payload.snapshots === 'object' ? payload.snapshots : {};

  Object.entries(snapshots).forEach(([key, snapshot]) => {
    inventorySnapshotStore.set(key, snapshot);
  });
}

function getRetryKey(proxyBase, variantIds) {
  return `${proxyBase}::${variantIds.slice().sort().join(',')}`;
}

function clearRetryState(retryKey) {
  const existingTimer = inventoryRetryTimers.get(retryKey);

  if (existingTimer) {
    window.clearTimeout(existingTimer);
  }

  inventoryRetryTimers.delete(retryKey);
  inventoryRetryCounts.delete(retryKey);
}

function applyInventoryGroup(group) {
  group.cards.forEach((widget) => {
    updateCardWidget(
      widget,
      getAggregateCardSnapshot(widget.dataset.variantIds || widget.dataset.variantId),
    );
  });

  group.products.forEach((widget) => {
    const selectedVariantId = widget.dataset.selectedVariantId || parseVariantIds(widget.dataset.variantIds)[0] || '';
    updateProductWidget(widget, getSnapshotForVariantId(selectedVariantId));
  });
}

function groupHasPendingSoldCounts(variantIds) {
  return variantIds.some((variantId) => hasPendingSoldCount(getSnapshotForVariantId(variantId)));
}

function scheduleRetry(proxyBase, variantIds) {
  const retryKey = getRetryKey(proxyBase, variantIds);
  const retryCount = inventoryRetryCounts.get(retryKey) || 0;

  if (retryCount >= INVENTORY_MAX_RETRY_COUNT || inventoryRetryTimers.has(retryKey)) {
    return;
  }

  const retryTimer = window.setTimeout(() => {
    inventoryRetryTimers.delete(retryKey);
    inventoryRetryCounts.set(retryKey, retryCount + 1);

    const groups = collectInventoryGroups();
    const group = groups.get(proxyBase);

    if (!group) {
      clearRetryState(retryKey);
      return;
    }

    void hydrateInventoryGroup(proxyBase, group, Array.from(group.variantIds));
  }, INVENTORY_RETRY_DELAY_MS * (retryCount + 1));

  inventoryRetryTimers.set(retryKey, retryTimer);
}

async function hydrateInventoryGroup(proxyBase, group, variantIds) {
  const retryKey = getRetryKey(proxyBase, variantIds);

  if (!variantIds.length) {
    clearRetryState(retryKey);
    return;
  }

  try {
    await fetchInventorySnapshots(proxyBase, variantIds);
  } catch (error) {
    console.error('[townwine-inventory] failed to fetch inventory snapshots', {
      proxyBase,
      variantIds,
      message: error instanceof Error ? error.message : String(error),
    });
    return;
  }

  applyInventoryGroup(group);

  if (groupHasPendingSoldCounts(variantIds)) {
    scheduleRetry(proxyBase, variantIds);
    return;
  }

  clearRetryState(retryKey);
}

async function hydrateInventory() {
  const groups = collectInventoryGroups();

  if (!groups.size) {
    return;
  }

  await Promise.all(
    Array.from(groups.entries()).map(([proxyBase, group]) => {
      return hydrateInventoryGroup(proxyBase, group, Array.from(group.variantIds));
    }),
  );
}

function scheduleHydration() {
  window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    hydrateInventory();
  }, 80);
}

function shouldRefreshForMutation(mutationList) {
  return mutationList.some((mutation) => {
    const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];

    return changedNodes.some((node) => {
      if (!(node instanceof HTMLElement)) {
        return false;
      }

      return (
        node.matches?.(CARD_SELECTOR) ||
        node.matches?.(PRODUCT_SELECTOR) ||
        Boolean(node.querySelector?.(CARD_SELECTOR)) ||
        Boolean(node.querySelector?.(PRODUCT_SELECTOR))
      );
    });
  });
}

function startObserver() {
  if (!(document.body instanceof HTMLElement) || domObserver) {
    return;
  }

  domObserver = new MutationObserver((mutationList) => {
    if (shouldRefreshForMutation(mutationList)) {
      scheduleHydration();
    }
  });

  domObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function initTownwineInventory() {
  scheduleHydration();
  startObserver();
  document.addEventListener(ThemeEvents.FilterUpdate, scheduleHydration);
  window.addEventListener('pageshow', scheduleHydration);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTownwineInventory, { once: true });
} else {
  initTownwineInventory();
}
